import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { InvoiceListItem } from './useInvoices';

export interface PatientInvoiceFilters {
  includeVoided?: boolean;
}

export const patientInvoicesQueryKey = (
  patientId: string | null | undefined,
  filters: PatientInvoiceFilters = {},
) => ['invoices', 'patient', patientId, filters] as const;

export function usePatientInvoices(
  patientId: string | null | undefined,
  filters: PatientInvoiceFilters = {},
) {
  return useQuery({
    queryKey: patientInvoicesQueryKey(patientId, filters),
    enabled: Boolean(patientId),
    queryFn: async (): Promise<InvoiceListItem[]> => {
      if (!patientId) return [];
      
      let q = supabase
        .from('invoices')
        .select(
          `id, clinic_id, patient_id, invoice_number, status, currency,
           subtotal_cents, discount_cents, vat_cents, total_cents,
           total_paid_cents, last_payment_at,
           issued_at, due_at, paid_at, voided_at, last_sent_at, last_reminder_sent_at, last_emailed_to,
           created_at, updated_at,
           sd_submitted_at, sd_uuid,
           patient:patients(first_name, last_name, pat_number)`,
        )
        .eq('patient_id', patientId)
        .order('created_at', { ascending: false });

      // Exclude voided invoices by default
      if (!filters.includeVoided) {
        q = q.neq('status', 'void');
      }

      const { data, error } = await q;
      if (error) throw error;

      return (data ?? []).map((row): InvoiceListItem => {
        const patient = (
          row as unknown as {
            patient: { first_name: string; last_name: string; pat_number: string | null } | null;
          }
        ).patient;
        return {
          id: row.id,
          clinic_id: row.clinic_id,
          patient_id: row.patient_id,
          invoice_number: row.invoice_number,
          status: row.status,
          currency: row.currency,
          subtotal_cents: row.subtotal_cents,
          discount_cents: row.discount_cents,
          vat_cents: row.vat_cents,
          total_cents: row.total_cents,
          total_paid_cents: row.total_paid_cents ?? 0,
          last_payment_at: row.last_payment_at ?? null,
          issued_at: row.issued_at,
          due_at: row.due_at,
          paid_at: row.paid_at,
          voided_at: row.voided_at,
          last_sent_at: row.last_sent_at ?? null,
          last_reminder_sent_at: row.last_reminder_sent_at ?? null,
          last_emailed_to: row.last_emailed_to ?? null,
          created_at: row.created_at,
          updated_at: row.updated_at,
          patient_first_name: patient?.first_name ?? null,
          patient_last_name: patient?.last_name ?? null,
          patient_pat_number: patient?.pat_number ?? null,
        };
      });
    },
    staleTime: 15_000,
  });
}
